import styles from './styles.module.css'

import React, { useEffect, useRef, useState } from 'react'
import { useDrop } from 'react-dnd'

import { AUDIO_FILE } from '@disco/constants/FileTypes'
import { FILE } from '@disco/constants/ItemTypes'
import {
  canFormatBeOptimised,
  getExtension,
  getFilesFromDraggedItem,
  getFileTypeByExt,
} from '@disco/utils/file-utils'

import { dndConnectRef } from 'utils/dnd-connect-ref'

import {
  Box,
  Button,
  Callout,
  Card,
  Checkbox,
  Dialog,
  Flex,
  Progress,
  Separator,
  Text,
  Theme,
} from 'components/ds'

import type { InboxSelection } from './inbox-track-picker'
import InboxTrackPicker from './inbox-track-picker'

type Track = Record<string, any>

type TrackUploader = {
  tempItem?: {
    tempId: string
    title?: string
    artist?: string
    album?: string
    progress?: number
  }
  uploadedToS3?: boolean
  filename?: string
}

type Props = {
  track: Track
  onClose: () => void
  onDone: () => void
  onUpload: (abortController: unknown) => void
  existingFormat?: string
  uploadTrackFileToS3: (file: File, uploadPolicyUrl: string) => unknown
  finalizeTrackUpload: (uploadPolicyUrl: string, transcode: boolean) => void
  format?: string
  trackUploader: TrackUploader
  isAdding?: boolean
}

type Selection =
  | { source: 'computer'; file: File; ext: string }
  | ({ source: 'inbox' } & InboxSelection)

type Step = 'choose' | 'replacing' | 'done'

const SIMULATED_DURATION_MS = 2000
const SIMULATED_TICK_MS = 100

function describeSource(selection: Selection) {
  return selection.source === 'computer'
    ? `"${selection.file.name}"`
    : `"${selection.track.name}" from ${selection.inboxName || 'your inbox'}`
}

/**
 * Dialog for replacing a track's file, or adding its missing format, from the
 * user's computer or one of their inbox tracks.
 * STL-1348 spike: the replace itself is simulated and the track is not changed.
 */
export default function UploadTrackFile(props: Props) {
  const { isAdding, track, onClose, onDone, format, existingFormat } = props
  const needsLossless = isAdding
    ? !canFormatBeOptimised(existingFormat ?? '')
    : canFormatBeOptimised(format ?? '')
  const kindLabel = needsLossless ? 'lossless' : 'MP3 or M4A'

  const [selection, setSelection] = useState<Selection | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [transcode, setTranscode] = useState(true)
  const [step, setStep] = useState<Step>('choose')
  const [progress, setProgress] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (step !== 'replacing') return
    const interval = setInterval(() => {
      setProgress(current => {
        const next = current + (100 * SIMULATED_TICK_MS) / SIMULATED_DURATION_MS
        if (next >= 100) {
          clearInterval(interval)
          setStep('done')
          return 100
        }
        return next
      })
    }, SIMULATED_TICK_MS)
    return () => clearInterval(interval)
  }, [step])

  const handleComputerFiles = (files: File[]) => {
    const [file] = files
    if (!file) return
    const ext = getExtension(file.name)
    if (canFormatBeOptimised(ext) !== needsLossless) {
      setFileError(
        `"${file.name}" isn't a ${kindLabel} file. Choose a ${kindLabel} file.`,
      )
      return
    }
    setFileError(null)
    setSelection({ source: 'computer', file, ext })
  }

  const [{ isDragging }, dropRef] = useDrop<any, void, { isDragging: boolean }>(
    () => ({
      accept: FILE,
      collect: monitor => ({ isDragging: monitor.isOver() }),
      drop: (draggedItem, monitor) => {
        if (monitor.didDrop()) return
        if (!draggedItem?.items) return
        getFilesFromDraggedItem(draggedItem).then(handleComputerFiles)
      },
    }),
    [needsLossless],
  )

  const optimisedFormat =
    getFileTypeByExt(format || existingFormat) === AUDIO_FILE ? 'mp3' : 'mp4'
  const showTranscode = Boolean(
    selection && canFormatBeOptimised(selection.ext),
  )
  const actionLabel = isAdding ? 'Add' : 'Replace'

  const sourceDescription = selection ? describeSource(selection) : ''
  const progressMessage = `${
    isAdding ? 'Adding' : 'Replacing'
  } with ${sourceDescription}…`
  const doneMessage = isAdding
    ? `Added a .${selection?.ext} to "${track.name}" using ${sourceDescription}.`
    : `Replaced the .${format} of "${track.name}" with ${sourceDescription}.`

  const title = isAdding
    ? `Add format for ${track.name}`
    : `Replace file for ${track.name} (${format})`

  const renderChoose = () => (
    <Flex direction="column" gap="4" className={styles.fill}>
      <Card
        ref={dndConnectRef(dropRef)}
        className={styles.dropCard}
        data-dragging={isDragging}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={event => {
            handleComputerFiles(Array.from(event.target.files ?? []))
            if (fileInputRef.current) fileInputRef.current.value = ''
          }}
        />
        <Flex direction="column" align="center" gap="1" py="4">
          <Text size="2" weight="medium">
            {selection?.source === 'computer'
              ? selection.file.name
              : 'Drag and drop a file, or click to browse'}
          </Text>
          <Text size="1" color="gray">
            From your computer
          </Text>
        </Flex>
      </Card>

      {fileError ? (
        <Callout.Root color="red" size="1">
          <Callout.Text>{fileError}</Callout.Text>
        </Callout.Root>
      ) : null}

      <Flex align="center" gap="3">
        <Separator size="4" />
        <Text size="1" color="gray" wrap="nowrap">
          Or choose from your inbox
        </Text>
        <Separator size="4" />
      </Flex>

      <InboxTrackPicker
        needsLossless={needsLossless}
        selectedTrackId={
          selection?.source === 'inbox' ? selection.track.id : null
        }
        onSelect={inboxSelection => {
          setFileError(null)
          setSelection({ source: 'inbox', ...inboxSelection })
        }}
      />

      {showTranscode ? (
        <Text as="label" size="2">
          <Flex gap="2" align="center">
            <Checkbox
              checked={transcode}
              onCheckedChange={checked => setTranscode(checked === true)}
            />
            {`Create a new ${optimisedFormat} copy of this track to replace existing ${optimisedFormat}`}
          </Flex>
        </Text>
      ) : null}
    </Flex>
  )

  const renderReplacing = () => (
    <Flex direction="column" gap="3" py="4">
      <Text size="2">{progressMessage}</Text>
      <Progress value={progress} />
    </Flex>
  )

  const renderDone = () => (
    <Box py="4">
      <Text size="2">{doneMessage}</Text>
    </Box>
  )

  return (
    <Dialog.Root open onOpenChange={() => {}}>
      <Dialog.Content
        maxWidth="600px"
        aria-describedby={undefined}
        className={styles.dialog}
      >
        <Theme className={styles.fill}>
          <Dialog.Title>{title}</Dialog.Title>
          <Box mt="4" className={styles.fill}>
            {step === 'choose' ? renderChoose() : null}
            {step === 'replacing' ? renderReplacing() : null}
            {step === 'done' ? renderDone() : null}
          </Box>
          <Flex gap="3" mt="5" justify="end">
            {step === 'choose' ? (
              <>
                <Button variant="soft" color="gray" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  disabled={!selection}
                  onClick={() => {
                    setProgress(0)
                    setStep('replacing')
                  }}
                >
                  {actionLabel}
                </Button>
              </>
            ) : null}
            {step === 'done' ? <Button onClick={onDone}>Done</Button> : null}
          </Flex>
        </Theme>
      </Dialog.Content>
    </Dialog.Root>
  )
}
